// Zod schemas shared across auth forms (login, signup, OTP, password reset).
// The same schemas are used for AntD Form validation and as the parsing layer
// before hitting authService — guarantees the request body always matches the
// shape the backend expects.
import { z } from 'zod';
import { OTP_PURPOSE } from '@core/constants/auth';
import { CUSTOMER_TYPE, requiresVatNumber } from '@core/constants/platform';

const email = z.string().trim().toLowerCase().email('Enter a valid email');
const password = z.string().min(8, 'Password must be at least 8 characters');

export const mobileSchema = z
  .object({
    country_code: z.string().min(1).max(5).optional(),
    calling_code: z.string().min(1).max(5).optional(),
    number: z
      .string()
      .trim()
      .min(6, 'Enter a valid phone number')
      .max(15, 'Enter a valid phone number')
      .optional(),
  })
  .optional();

export const loginSchema = z
  .object({
    email: email.optional(),
    mobile: mobileSchema,
    password: z.string().min(1, 'Password is required'),
  })
  .refine((d) => Boolean(d.email) || Boolean(d.mobile?.number), {
    message: 'Email or phone is required',
    path: ['email'],
  });

const required = (message) => z.string().trim().min(1, message);

// B2B signup (`ecommerce-b2b`). Every identifier is mandatory; a VAT country
// collects — and requires — a VAT number in place of the GSTIN.
export const businessDetailsSchema = z
  .object({
    business_name: required('Business name is required'),
    business_type: required('Business type is required'),
    business_address: z.object({
      line: required('Address line is required'),
      land_mark: z.string().trim().optional(),
      city: required('City is required'),
      state: required('State is required'),
      zip_code: z.string().trim().optional(),
      country: z.string().trim().optional(),
    }),
    business_phone: z.object({
      calling_code: z.string().trim().optional(),
      number: required('Business phone number is required'),
    }),
    business_email: z.string().trim().toLowerCase().email('Enter a valid business email'),
    trn: required('TRN is required'),
    crn: required('CRN is required'),
    service_tax_number: required('Service tax number is required'),
    gstin: z.string().trim().optional(),
    vat_number: z.string().trim().optional(),
  })
  .superRefine((d, ctx) => {
    const isVatCountry = requiresVatNumber(d.business_address.country);
    if (isVatCountry && !d.vat_number) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VAT number is required',
        path: ['vat_number'],
      });
    }
    if (!isVatCountry && !d.gstin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'GSTIN is required',
        path: ['gstin'],
      });
    }
  });

export const signupSchema = z
  .object({
    first_name: z.string().trim().min(1, 'First name is required'),
    last_name: z.string().trim().optional(),
    email,
    password,
    confirmPassword: z.string(),
    mobile: mobileSchema,
    referral_code: z.string().trim().optional(),
    type: z.enum([CUSTOMER_TYPE.INDIVIDUAL, CUSTOMER_TYPE.BUSINESS]).optional(),
    business_details: businessDetailsSchema.optional(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((d) => d.type !== CUSTOMER_TYPE.BUSINESS || Boolean(d.business_details), {
    message: 'Business details are required',
    path: ['business_details', 'business_name'],
  });

export const verifyOtpSchema = z
  .object({
    email,
    otp: z
      .string()
      .trim()
      .regex(/^\d{4,6}$/, 'OTP must be 4–6 digits'),
    purpose: z.enum([OTP_PURPOSE.SIGNUP, OTP_PURPOSE.RESET_PASSWORD]),
    token: z.string().trim().optional(),
    password: password.optional(),
  })
  .refine((d) => d.purpose !== OTP_PURPOSE.RESET_PASSWORD || Boolean(d.password), {
    message: 'New password is required',
    path: ['password'],
  });

export const forgotPasswordSchema = z.object({
  email,
});

export const resendOtpSchema = z.object({
  email,
});
